import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { playScanBeep, vibrateScanResult } from '../utils/scanSounds';

const BANNER_TTL = { success: 2500, error: 4000 };
const FLASH_MS = 1200;

export function computeFulfillmentProgress(lines) {
  const total = lines.reduce((s, l) => s + (Number(l.total) || 0), 0);
  const remaining = lines.reduce((s, l) => s + (Number(l.remaining) || 0), 0);
  const verified = total - remaining;
  const openLines = lines.filter((l) => l.remaining > 0).length;
  return {
    total,
    remaining,
    verified,
    openLines,
    lineCount: lines.length,
    pct: total ? verified / total : 0,
  };
}

export function useScanFeedback({ lines, beepEnabled }) {
  const [lastScan, setLastScan] = useState(null);
  const [flashLineId, setFlashLineId] = useState(null);
  const [shake, setShake] = useState(false);
  const timersRef = useRef([]);

  const progress = useMemo(() => computeFulfillmentProgress(lines), [lines]);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  const reset = useCallback(() => {
    clearTimers();
    setLastScan(null);
    setFlashLineId(null);
    setShake(false);
  }, [clearTimers]);

  const reportSuccess = useCallback(
    (line) => {
      clearTimers();
      setLastScan({
        status: 'success',
        code: line.sku,
        lineId: line.id,
        label: line.label,
        remaining: line.remaining,
        total: line.total,
        at: Date.now(),
      });
      setFlashLineId(line.id);
      timersRef.current.push(setTimeout(() => setFlashLineId(null), FLASH_MS));
      timersRef.current.push(setTimeout(() => setLastScan(null), BANNER_TTL.success));
      if (beepEnabled) {
        playScanBeep('success');
        vibrateScanResult(true);
      }
    },
    [beepEnabled, clearTimers],
  );

  const reportError = useCallback(
    (payload) => {
      clearTimers();
      setLastScan({
        status: 'error',
        ...payload,
        at: Date.now(),
      });
      setShake(true);
      timersRef.current.push(setTimeout(() => setShake(false), 450));
      timersRef.current.push(setTimeout(() => setLastScan(null), BANNER_TTL.error));
      if (beepEnabled) {
        playScanBeep('error');
        vibrateScanResult(false);
      }
    },
    [beepEnabled, clearTimers],
  );

  useEffect(() => () => clearTimers(), [clearTimers]);

  return { lastScan, flashLineId, shake, progress, reportSuccess, reportError, reset };
}
